import axios from 'axios';
import client from './client';
import qs from 'qs';

const API_BASE = process.env.REACT_APP_API_URL || ''; // 환경변수로 베이스 URL 관리
const estimateUrl = '/api/v1/chatbot/estimate';
const chatRoomUrl = '/api/v1/chatrooms';

// ============================================
// 채팅방 관리 API
// ============================================

/**
 * 전체 채팅방 목록 조회
 * @returns {Promise<ChatRoomDto[]>}
 */
export const getAllChatRooms = async () => {
  try {
    const response = await client.get(chatRoomUrl);
    return response.data.list || [];
  } catch (error) {
    console.error('채팅방 목록 조회 실패:', error);
    throw error;
  }
};

/**
 * 내가 참여한 채팅방 목록 조회
 * @returns {Promise<ChatRoomDto[]>}
 */
export const getMyChatRooms = async () => {
  try {
    const response = await client.get(`${chatRoomUrl}/my`);
    return response.data.list || [];
  } catch (error) {
    console.error('내 채팅방 목록 조회 실패:', error);
    throw error;
  }
};

/**
 * 채팅방 생성
 * @param {object} chatRoom { name, chatRoomTyp, participants }
 * @returns {Promise<ChatRoomDto>}
 */
export const createChatRoom = async (chatRoom) => {
  try {
    const response = await client.post(chatRoomUrl, chatRoom);
    return response.data.info;
  } catch (error) {
    console.error('채팅방 생성 실패:', error);
    throw error;
  }
};

/**
 * 채팅방 상세 조회 (참여자 포함)
 * @param {string} chatRoomUuid 
 * @returns {Promise<ChatRoomDto>}
 */
export const getChatRoomByUuid = async (chatRoomUuid) => {
  try {
    const response = await client.get(`${chatRoomUrl}/${chatRoomUuid}`);
    return response.data.info;
  } catch (error) {
    console.error('채팅방 조회 실패:', error);
    throw error;
  }
};

/**
 * 채팅방 수정
 * @param {string} chatRoomUuid 
 * @param {object} chatRoom 
 * @returns {Promise<ChatRoomDto>}
 */
export const updateChatRoom = async (chatRoomUuid, chatRoom) => {
  try {
    const response = await client.put(`${chatRoomUrl}/${chatRoomUuid}`, chatRoom);
    return response.data.info;
  } catch (error) {
    console.error('채팅방 수정 실패:', error);
    throw error;
  }
};

/**
 * 채팅방 삭제
 * @param {string} chatRoomUuid 
 * @returns {Promise<void>}
 */
export const deleteChatRoom = async (chatRoomUuid) => {
  try {
    await client.delete(`${chatRoomUrl}/${chatRoomUuid}`);
  } catch (error) {
    console.error('채팅방 삭제 실패:', error);
    throw error;
  }
};

/**
 * 채팅방 검색
 * @param {string} keyword 
 * @returns {Promise<ChatRoomDto[]>}
 */
export const searchChatRooms = async (keyword) => {
  try {
    const response = await client.get(`${chatRoomUrl}/search`, { params: { keyword } });
    return response.data.list || [];
  } catch (error) {
    console.error('채팅방 검색 실패:', error);
    throw error;
  }
};

/**
 * 채팅방 나가기
 * @param {string} chatRoomUuid 
 * @returns {Promise<void>}
 */
export const leaveChatRoom = async (chatRoomUuid) => {
  try {
    await client.delete(`${chatRoomUrl}/${chatRoomUuid}/leave`);
  } catch (error) {
    console.error('채팅방 나가기 실패:', error);
    throw error;
  }
};

// ============================================
// 채팅방 참여자 관리 API
// ============================================

/**
 * 채팅방 참여자 목록 조회
 * @param {string} chatRoomUuid 
 * @returns {Promise<ChatRoomParticipantDto[]>}
 */
export const getParticipants = async (chatRoomUuid) => {
  try {
    const response = await client.get(`${chatRoomUrl}/${chatRoomUuid}/participants`);
    return response.data.list || [];
  } catch (error) {
    console.error('참여자 목록 조회 실패:', error);
    throw error;
  }
};

/**
 * 참여자 추가 (초대)
 * @param {string} chatRoomUuid 
 * @param {object} participant { usrUuid, ruleTyp }
 * @returns {Promise<void>}
 */
export const addParticipant = async (chatRoomUuid, participant) => {
  try {
    await client.post(`${chatRoomUrl}/${chatRoomUuid}/participants`, participant);
  } catch (error) {
    console.error('참여자 추가 실패:', error);
    throw error;
  }
};

/**
 * 여러 참여자 일괄 추가
 * @param {string} chatRoomUuid 
 * @param {string[]} usrUuids 
 * @returns {Promise<void>}
 */
export const addParticipants = async (chatRoomUuid, usrUuids) => {
  try {
    await client.post(`${chatRoomUrl}/${chatRoomUuid}/participants/batch`, usrUuids);
  } catch (error) {
    console.error('참여자 일괄 추가 실패:', error);
    throw error;
  }
};

/**
 * 참여자 제거 (강퇴)
 * @param {string} chatRoomUuid 
 * @param {string} usrUuid 
 * @returns {Promise<void>}
 */
export const removeParticipant = async (chatRoomUuid, usrUuid) => {
  try {
    await client.delete(`${chatRoomUrl}/${chatRoomUuid}/participants/${usrUuid}`);
  } catch (error) {
    console.error('참여자 제거 실패:', error);
    throw error;
  }
};

/**
 * OWNER 이양
 * @param {string} chatRoomUuid
 * @param {string} newOwnerUuid  새 OWNER가 될 멤버 UUID
 * @returns {Promise<void>}
 */
export const transferOwnership = async (chatRoomUuid, newOwnerUuid) => {
  try {
    await client.post(`${chatRoomUrl}/${chatRoomUuid}/participants/transfer-ownership`, { newOwnerUuid });
  } catch (error) {
    console.error('OWNER 이양 실패:', error);
    throw error;
  }
};

/**
 * 참여자 역할 변경
 * @param {string} chatRoomUuid 
 * @param {string} usrUuid 
 * @param {string} ruleTyp 
 * @returns {Promise<void>}
 */
export const updateParticipantRole = async (chatRoomUuid, usrUuid, ruleTyp) => {
  try {
    await client.put(`${chatRoomUrl}/${chatRoomUuid}/participants/${usrUuid}/role`, { ruleTyp });
  } catch (error) {
    console.error('참여자 역할 변경 실패:', error);
    throw error;
  }
};

// ============================================
// 기존 채팅 메시지 API
// ============================================

/**
 * 채팅방의 기존 메시지 리스트를 가져옵니다.
 * @param {string} roomId 채팅방 UUID
 * @param {string} [token] JWT 토큰 (optional)
 * @returns {Promise<any[]>}
 */
export function fetchChatHistory(roomId, token, range) {
  // client 인스턴스 사용으로 통일 (interceptor 적용, 인증 자동 처리)
  // range를 주면 그 구간만 받는다. 방 전체를 한 번에 읽으면 메시지가 쌓일수록 느려지므로,
  // 화면은 최근 구간만 먼저 받고 필요할 때 이전 구간을 이어 받는다.
  const params = {};
  if (range?.since) params.since = range.since;
  if (range?.before) params.before = range.before;
  if (range?.limit) params.limit = range.limit;

  return client
    .get(`/api/v1/messages/info/${roomId}`, { params })
    .then(response => response.data);
}

/**
 * 채팅으로 업로드한 파일을 영업 프로젝트에 연결합니다.
 * @param {string} fileUuid 파일 UUID
 * @param {string} prjUuid 프로젝트 UUID
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const linkChatFileToProject = (fileUuid, prjUuid) =>
  client.post(`/api/v1/messages/info/file/${fileUuid}/project`, { prjUuid });

/**
 * 현재 사용자가 파일을 연결할 수 있는 활성 영업 프로젝트를 검색합니다.
 * @param {string} keyword 프로젝트 번호/사업명/매출처 검색어
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const searchChatFileProjects = (keyword = '') =>
  client.get('/api/v1/messages/info/file/projects', { params: { keyword } });

/**
 * 채팅에 업로드한 도면 이미지로 유사 공정(전극/공구 내역)을 검색합니다.
 * VLM 분석은 비동기로 처리되며, 결과는 채팅 웹소켓 메시지로 도착합니다.
 * @param {string} fileUuid 파일 UUID
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const requestMachiningSimilarByFile = (fileUuid) =>
  client.post(`/api/v1/messages/info/file/${fileUuid}/machining-similar`);

/**
 * 채팅에 업로드한 가공 툴시트 엑셀을 기준자료와 대조해 행별로 검토합니다.
 * 검토는 비동기로 처리되며, 결과는 채팅 웹소켓 메시지(TOOLSHEET_REVIEW)로 도착합니다.
 * @param {string} fileUuid 파일 UUID
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const requestToolSheetReviewByFile = (fileUuid) =>
  client.post(`/api/v1/messages/info/file/${fileUuid}/toolsheet-review`);

/**
 * 채팅에 업로드한 STP/STEP 3D 설계파일의 형상을 분석합니다(치수/부피/6면도).
 * 분석은 비동기로 처리되며, 결과는 채팅 웹소켓 메시지(STP_SHAPE_ANALYSIS)로 도착합니다.
 * @param {string} fileUuid 파일 UUID
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export const requestShapeAnalysisByFile = (fileUuid) =>
  client.post(`/api/v1/messages/info/file/${fileUuid}/shape-analysis`);

/**
 * 공구 사양/유사 공정 검색 결과 카드의 도면·형상 썸네일을 가져옵니다.
 * @param {string} path 썸네일 상대 경로 (item.thumbnailPath)
 * @returns {Promise<import('axios').AxiosResponse>} responseType: 'blob'
 */
export const fetchMachiningThumbnail = (path) =>
  client.get('/api/v1/chat/machining-rag/thumbnail', { params: { path }, responseType: 'blob' });

/**
 * 공구 사양/유사 공정 결과의 원본 엑셀·PDF 파일을 받는다(RAG 데이터 폴더 안 파일만).
 * @param {string} path 원본 상대 경로 (item.sourcePath)
 */
export const downloadMachiningSource = (path) =>
  client.get('/api/v1/chat/machining-rag/source', { params: { path }, responseType: 'blob' });

// ============================================
// 견적서 관련 API
// ============================================

/**
 * 견적서 등록 폼 요청 (직접 API 호출)
 * @param {string} tempQteUuid 임시 견적서 UUID
 * @returns {Promise<BotResponseDto>}
 */
export const getEstimateForm = async (tempQteUuid, confirmationToken) => {
  try {
    const queryString = qs.stringify({ tempQteUuid, confirmationToken });
    const response = await client.post(`${estimateUrl}/form?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('견적서 폼 요청 실패:', error);
    throw error;
  }
};

/**
 * 견적서 등록 제출 (직접 API 호출)
 * @param {string} tempQteUuid 임시 견적서 UUID
 * @param {object} quoteData 견적서 데이터
 * @returns {Promise<BotResponseDto>}
 */
export const submitEstimate = async (tempQteUuid, quoteData, confirmationToken) => {
  try {
    const queryString = qs.stringify({ tempQteUuid, confirmationToken });
    const response = await client.post(`${estimateUrl}/submit?${queryString}`, quoteData);
    return response.data;
  } catch (error) {
    console.error('견적서 등록 실패:', error);
    throw error;
  }
};

/**
 * 견적서 등록 취소 (직접 API 호출)
 * @param {string} tempQteUuid 임시 견적서 UUID
 * @returns {Promise<BotResponseDto>}
 */
export const cancelEstimate = async (tempQteUuid, confirmationToken) => {
  try {
    const queryString = qs.stringify({ tempQteUuid, confirmationToken });
    const response = await client.post(`${estimateUrl}/cancel?${queryString}`);
    return response.data;
  } catch (error) {
    console.error('견적서 취소 실패:', error);
    throw error;
  }
};

/**
 * 채팅 메시지를 통한 견적서 처리 (기존 방식과 호환)
 * @param {function} onSend 메시지 전송 함수
 * @param {string} action 액션 타입 ('FORM_REQUEST', 'SUBMIT', 'CANCEL')
 * @param {string} tempQteUuid 임시 견적서 UUID
 * @param {object} data 추가 데이터 (SUBMIT 시 견적서 데이터)
 */
export const sendEstimateMessage = (onSend, action, tempQteUuid, data = null, confirmationToken = '') => {
  let message;
  
  switch (action) {
    case 'FORM_REQUEST':
      message = `견적서 등록 폼 요청: ${tempQteUuid}|${confirmationToken}`;
      break;
    case 'CANCEL':
      message = `견적서 등록 취소: ${tempQteUuid}|${confirmationToken}`;
      break;
    case 'SUBMIT':
      message = `견적서 등록 제출: ${tempQteUuid}|${confirmationToken}|${JSON.stringify(data)}`;
      break;
    default:
      console.error('알 수 없는 견적서 액션:', action);
      return;
  }
  
  if (onSend) {
    onSend(message);
  }
};

// ============================================
// 헬퍼 함수
// ============================================

/**
 * 에러 응답 생성 헬퍼
 * @param {string} message 에러 메시지
 * @returns {object} BotResponseDto 형태의 에러 응답
 */
export const createErrorResponse = (message) => ({
  responseType: 'ERROR',
  message: `❌ ${message}`,
  data: null,
  hasAttachment: false
});

/**
 * 로딩 응답 생성 헬퍼
 * @param {string} message 로딩 메시지
 * @returns {object} BotResponseDto 형태의 로딩 응답
 */
export const createLoadingResponse = (message) => ({
  responseType: 'LOADING',
  message: `⏳ ${message}`,
  data: null,
  hasAttachment: false
});

// 견적서 관련 상수
export const ESTIMATE_ACTIONS = {
  FORM_REQUEST: 'FORM_REQUEST',
  SUBMIT: 'SUBMIT',
  CANCEL: 'CANCEL'
};

export const ESTIMATE_RESPONSE_TYPES = {
  CONFIRM: 'ESTIMATE_REGISTRATION_CONFIRM',
  FORM: 'ESTIMATE_REGISTRATION_FORM',
  COMPLETE: 'ESTIMATE_REGISTRATION_COMPLETE',
  ERROR: 'ERROR',
  LOADING: 'LOADING'
};

// 채팅방 유형 상수
export const CHAT_ROOM_TYPES = {
  NORMAL: 'NORMAL',
  PROJECT: 'PROJECT',
  TEAM: 'TEAM'
};

// 참여자 역할 상수 (OWNER / MEMBER 2단계)
export const PARTICIPANT_ROLES = {
  OWNER: 'OWNER',
  MEMBER: 'MEMBER',
};

/** 역할별 한글 레이블 */
export const ROLE_LABEL = {
  OWNER: '소유자',
  MEMBER: '멤버',
};

/** 관리 권한 여부 (OWNER만) */
export const canManage = (role) => role === PARTICIPANT_ROLES.OWNER;
